// ============================================================================
// Funcionalidades:
// - Cadastro de novo paciente (nome, CPF, telefone, email)
// - Seleção de empresa, profissional, data, horário e serviço
// - Filtros em cascata (empresa → profissional → data → horário → serviço)
// - Busca de paciente existente (via botão de pesquisa)
// - Validação de campos obrigatórios
// - Integração com API para criar agendamento
// - Design responsivo (Desktop 2 colunas, Mobile 1 coluna)
// ============================================================================

import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputMask } from "primereact/inputmask";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../AuthContext";
import {
  FaUser,
  FaIdCard,
  FaPhone,
  FaEnvelope,
  FaBuilding,
  FaUserMd,
  FaCalendarAlt,
  FaClock,
  FaList,
  FaCalendarCheck,
} from "react-icons/fa";

const DialogNovaAgenda = ({
  // Controle do diálogo
  visible,
  onHide,

  // Estados do formulário
  novoPaciente,
  setNovoPaciente,
  empresa,
  setEmpresa,
  profissional,
  setProfissional,
  data,
  setData,
  hora,
  setHora,
  servico,
  setServico,

  // Dados e filtros
  dadosCompletos,
  profissionaisFiltrados,
  horariosDisponiveis,
  setHorariosDisponiveis,
  servicosFiltrados,

  // Callbacks para o componente pai
  setReservaEditando,
  setAgendamentoCriadoId,
  setShowConfirmFinalizarAposCriacao,
  fetchAgendamentos,
  fetchTodosDados,
  showSuccessMessage,
  showErrorMessage,
  resetarCampos,
  setVisiblePesquisar,

  // Configurações
  checked,
  dataVeioDoCalendario,
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // useEffect(() => {
  //   if (visible) {
  //     console.log("🔍 DialogNovaAgenda ABERTO");
  //     console.log("data recebida via prop:", data);
  //     console.log("dataVeioDoCalendario:", dataVeioDoCalendario);
  //   }
  // }, [visible, data, dataVeioDoCalendario]);

  // ==========================================================================
  // DETECÇÃO DE DISPOSITIVO MÓVEL
  // ==========================================================================
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // ==========================================================================
  // FUNÇÃO: Cancelar - Limpa todos os campos do formulário
  // ==========================================================================
  const handleCancelar = () => {
    // Limpar dados do paciente
    setNovoPaciente({
      nome: "",
      cpf: "",
      telefone: "",
      email: "",
    });

    // Limpar campos de agendamento
    setEmpresa("");
    setProfissional("");
    setData("");
    setHora("");
    setServico("");
    setHorariosDisponiveis([]);

    // Chamar o onHide do pai para fechar o diálogo
    onHide();
  };

  // ==========================================================================
  // FUNÇÃO: Filtrar horários por profissional e data (versão local)
  // ==========================================================================
  const filtrarHorariosPorProfissionalEDataLocal = useCallback(
    (profissionalId, dataSelecionada) => {
      if (!dataSelecionada) {
        setHorariosDisponiveis([]);
        setHora("");
        return;
      }

      const dataObj = new Date(dataSelecionada + "T00:00:00");
      const diaSemana = dataObj.getDay();

      if (!profissionalId) {
        const horariosCadastrados = dadosCompletos.horarios
          .filter((horario) => parseInt(horario.dia_semana) === diaSemana)
          .map((horario) => horario.hora);

        const horariosUnicos = [...new Set(horariosCadastrados)].sort();
        const horariosFormatados = horariosUnicos.map((h) => ({
          label: h,
          value: h,
        }));

        setHorariosDisponiveis(horariosFormatados);
        setHora("");
        return;
      }

      const horariosCadastrados = dadosCompletos.horarios.filter(
        (horario) =>
          horario.profissional_id?.toString() === profissionalId?.toString() &&
          parseInt(horario.dia_semana) === diaSemana,
      );

      const horariosOcupados = dadosCompletos.agendamentos_existentes
        .filter(
          (agendamento) =>
            agendamento.data_atendimento === dataSelecionada &&
            agendamento.status_reserva !== "cancelada",
        )
        .map((agendamento) => agendamento.horario);

      let horariosDisponiveisFiltrados = horariosCadastrados
        .map((horario) => horario.hora)
        .filter((horario) => !horariosOcupados.includes(horario));

      // Verificar horários já passados
      if (!dataVeioDoCalendario) {
        const agora = new Date();
        const hoje = new Date().toISOString().split("T")[0];

        // Só remove horários passados se a data selecionada for hoje
        if (dataSelecionada === hoje) {
          const horaAtual = agora.getHours();
          const minutoAtual = agora.getMinutes();

          horariosDisponiveisFiltrados = horariosDisponiveisFiltrados.filter(
            (horario) => {
              const [horaStr, minutoStr] = horario.split(":");
              const horaHorario = parseInt(horaStr);
              const minutoHorario = parseInt(minutoStr);

              // Comparar horas e minutos
              if (horaHorario > horaAtual) return true;
              if (horaHorario === horaAtual && minutoHorario > minutoAtual)
                return true;
              return false;
            },
          );
        }
      }

      const horariosOrdenados = [...new Set(horariosDisponiveisFiltrados)]
        .sort()
        .map((horario) => ({
          label: horario,
          value: horario,
        }));

      setHorariosDisponiveis(horariosOrdenados);
      setHora("");
    },
    [
      dadosCompletos.horarios,
      dadosCompletos.agendamentos_existentes,
      dataVeioDoCalendario,
      setHorariosDisponiveis,
    ],
  );

  // ==========================================================================
  // EFFECT: Carregar horários quando profissional ou data mudarem
  // ==========================================================================
  useEffect(() => {
    if (visible && dadosCompletos.horarios?.length > 0) {
      if (profissional && data) {
        filtrarHorariosPorProfissionalEDataLocal(profissional, data);
      } else if (data) {
        filtrarHorariosPorProfissionalEDataLocal(null, data);
      }
    }
  }, [
    profissional,
    data,
    visible,
    dadosCompletos.horarios,
    filtrarHorariosPorProfissionalEDataLocal,
  ]);

  // ==========================================================================
  // FUNÇÃO: Buscar e setar o serviço padrão do profissional
  // ==========================================================================
  const setarServicoPadrao = useCallback(
    (profissionalId) => {
      // Buscar o profissional selecionado nos dados completos
      const profissionalSelecionado = dadosCompletos.profissionais?.find(
        (prof) => prof.id?.toString() === profissionalId?.toString(),
      );

      if (profissionalSelecionado && profissionalSelecionado.servicos) {
        // Buscar o serviço marcado como padrão (padrao === "1" ou padrao === 1)
        const servicoPadrao = profissionalSelecionado.servicos.find(
          (s) => s.padrao === "1" || s.padrao === 1,
        );

        if (servicoPadrao) {
          setServico(servicoPadrao.id);
        } else {
          setServico(null);
        }
      } else {
        setServico(null);
      }
    },
    [dadosCompletos.profissionais, setServico],
  );

  // ==========================================================================
  // EFFECT: Garantir que o serviço padrão seja mantido após filtro
  // ==========================================================================
  useEffect(() => {
    if (profissional && servicosFiltrados.length > 0 && !servico) {
      const profissionalSelecionado = dadosCompletos.profissionais?.find(
        (prof) => prof.id?.toString() === profissional?.toString(),
      );

      if (profissionalSelecionado && profissionalSelecionado.servicos) {
        const servicoPadrao = profissionalSelecionado.servicos.find(
          (s) => s.padrao === "1" || s.padrao === 1,
        );

        if (
          servicoPadrao &&
          servicosFiltrados.some((s) => s.value === servicoPadrao.id)
        ) {
          setServico(servicoPadrao.id);
        }
      }
    }
  }, [
    profissional,
    servicosFiltrados,
    servico,
    dadosCompletos.profissionais,
    setServico,
  ]);

  // ==========================================================================
  // EFFECT: Limpar formulário quando o diálogo for aberto
  // ==========================================================================
  useEffect(() => {
    if (visible) {
      // Só limpa os campos se NÃO veio do calendário
      if (!dataVeioDoCalendario) {
        setNovoPaciente({
          nome: "",
          cpf: "",
          telefone: "",
          email: "",
        });
        setEmpresa("");
        setProfissional("");
        setData("");
        setHora("");
        setServico("");
        setHorariosDisponiveis([]);
      }
    }
  }, [visible, dataVeioDoCalendario]);

  // ==========================================================================
  // HANDLER: Adicionar novo agendamento (chamada à API)
  // ==========================================================================
  const handleAdicionarAgenda = async () => {
    setLoading(true);

    try {
      const dataHora = `${data} ${hora}:00`;

      const payload = {
        empresa_id: empresa,
        profissional_id: profissional,
        servico_id: servico,
        data_hora: dataHora,
        tipo_agendamento: "primeira_consulta",
        paciente: {
          nome: novoPaciente.nome,
          cpf: novoPaciente.cpf,
          telefone: novoPaciente.telefone || "",
          email: novoPaciente.email || "",
        },
      };

      const response = await fetch(
        "https://helder.cantarellisolucoes.com.br/wp-json/custom/v1/agendaPaciente",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
            Accept: "application/json",
          },
          body: JSON.stringify(payload),
        },
      );

      const result = await response.json();

      if (result.success) {
        setAgendamentoCriadoId(result.data.id);

        // Criar objeto do agendamento para retorno - GARANTA que paciente_id está presente!
        const agendamentoCriado = {
          id: result.data.id,
          paciente_nome: novoPaciente.nome,
          paciente_cpf: novoPaciente.cpf,
          paciente_id: result.data.paciente_id,  // ← VERIFIQUE se isso está vindo da API
          profissional_id: profissional,
          empresa_id: empresa,
          servico_id: servico,
          servico_nome: servicosFiltrados.find((s) => s.value === servico)?.nome,
          servico_valor: servicosFiltrados.find((s) => s.value === servico)?.valor,
          data_atendimento: data,
          horario: hora,
        };

        console.log("Agendamento criado:", agendamentoCriado); // ← DEBUG: veja se paciente_id existe
        setReservaEditando(agendamentoCriado);

        // 🔥 LIMPAR CAMPOS DO FORMULÁRIO
        setNovoPaciente({
          nome: "",
          cpf: "",
          telefone: "",
          email: "",
        });
        setEmpresa("");
        setProfissional("");
        setData("");
        setHora("");
        setServico("");
        setHorariosDisponiveis([]);

        // Fechar o diálogo
        onHide();

        // Atualizar listas
        await fetchAgendamentos(checked);
        await fetchTodosDados();

        // Mostrar diálogo de confirmação para finalizar atendimento
        setShowConfirmFinalizarAposCriacao(true);
      } else {
        showErrorMessage(result.message || "Erro ao realizar agendamento");
      }
    } catch (error) {
      console.error("Erro:", error);
      showErrorMessage("Erro de conexão com o servidor");
    } finally {
      setLoading(false);
    }
  };

  // ==========================================================================
  // HANDLER: Mudança de data
  // ==========================================================================
  const handleDataChange = (e) => {
    if (e.value) {
      const ano = e.value.getFullYear();
      const mes = String(e.value.getMonth() + 1).padStart(2, "0");
      const dia = String(e.value.getDate()).padStart(2, "0");
      const dataFormatada = `${ano}-${mes}-${dia}`;
      setData(dataFormatada);

      // Buscar horários para a nova data
      if (profissional) {
        filtrarHorariosPorProfissionalEDataLocal(profissional, dataFormatada);
      } else {
        filtrarHorariosPorProfissionalEDataLocal(null, dataFormatada);
      }
    } else {
      setData(null);
      setHora("");
      setHorariosDisponiveis([]);
    }
  };

  // ==========================================================================
  // HANDLER: Mudança de profissional
  // ==========================================================================
  const handleProfissionalChange = (e) => {
    const profissionalId = e.value;
    setProfissional(profissionalId);
    setHora("");

    // 🔥 Setar serviço padrão do profissional
    setarServicoPadrao(profissionalId);
  };

  // ==========================================================================
  // ESTILOS COMPARTILHADOS
  // ==========================================================================
  const inputStyle = {
    width: "100%",
    height: "40px",
    fontFamily: "'Rajdhani', sans-serif",
    fontSize: "0.9rem",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    padding: "0 12px",
    transition: "all 0.2s",
    outline: "none",
  };

  const iconStyle = {
    color: "#B83732",
    fontSize: "1rem",
    minWidth: "20px",
  };

  const labelStyle = {
    fontWeight: "600",
    color: "#4a5568",
    fontSize: "0.85rem",
    letterSpacing: "0.3px",
    marginBottom: "6px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  };

  // ==========================================================================
  // TEMPLATES PARA DROPDOWNS (mantém consistência visual)
  // ==========================================================================
  const dropdownItemTemplate = (option) => (
    <span
      style={{
        fontFamily: "'Rajdhani', sans-serif",
        fontSize: "0.9rem",
        padding: "8px 12px",
      }}
    >
      {option.label}
    </span>
  );

  const dropdownValueTemplate = (option, props) => {
    if (!option)
      return (
        <span
          style={{
            fontFamily: "'Rajdhani', sans-serif",
            fontSize: "0.9rem",
            color: "#7f8c8d",
          }}
        >
          {props.placeholder}
        </span>
      );
    return (
      <span
        style={{
          fontFamily: "'Rajdhani', sans-serif",
          fontSize: "0.9rem",
        }}
      >
        {option.label}
      </span>
    );
  };

  // ==========================================================================
  // RENDER PRINCIPAL
  // ==========================================================================
  return (
    <Dialog
      visible={visible}
      style={{
        width: "90vw",
        maxWidth: "768px",
        fontFamily: "'Rajdhani', sans-serif",
      }}
      onHide={handleCancelar}
      draggable={false}
      header={
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "-25px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              width: "100%",
            }}
          >
            <div style={{ marginRight: "12px", paddingLeft: "8px" }}>
              <FaCalendarCheck
                style={{ fontSize: "2.4rem", color: "#B83732" }}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
              <div
                style={{
                  fontSize: "0.85rem",
                  width: "100%",
                  letterSpacing: "1px",
                  color: "#718096",
                }}
              >
                ADICIONAR
              </div>
              <div
                style={{
                  fontSize: "1.8rem",
                  fontWeight: "bold",
                  marginTop: "-8px",
                  color: "#2d3748",
                }}
              >
                AGENDAMENTO
              </div>
            </div>
            <div style={{ padding: "0 0 0 15px" }}>
              <Button
                icon="pi pi-search"
                className="p-button-outlined"
                onClick={() => {
                  setVisiblePesquisar(true);
                }}
                style={{
                  outline: "none",
                  boxShadow: "none",
                  padding: "8px 16px",
                  minWidth: "40px",
                  borderColor: "#e2e8f0",
                  color: "#B83732",
                  fontWeight: "500",
                  fontSize: "0.875rem",
                  borderRadius: "8px",
                }}
                disabled={loading}
              />
            </div>
          </div>
        </div>
      }
    >
      <>
        {/* CARD ÚNICO */}
        <div
          style={{
            backgroundColor: "#FAFAFA",
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            padding: "20px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          }}
        >
          {/* GRID RESPONSIVO: Desktop 2 colunas, Mobile 1 coluna */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
              gap: "16px",
            }}
          >
            {/* ========== COLUNA 1 - DADOS DO PACIENTE ========== */}

            {/* Nome */}
            <div style={{ display: "flex", flexDirection: "column" }}>
              <div style={labelStyle}>
                <FaUser style={iconStyle} />
                <span>NOME COMPLETO *</span>
              </div>
              <InputText
                value={novoPaciente.nome}
                onChange={(e) =>
                  setNovoPaciente({ ...novoPaciente, nome: e.target.value })
                }
                placeholder="Digite o nome completo"
                style={inputStyle}
              />
            </div>

            {/* CPF */}
            <div style={{ display: "flex", flexDirection: "column" }}>
              <div style={labelStyle}>
                <FaIdCard style={iconStyle} />
                <span>CPF *</span>
              </div>
              <InputMask
                placeholder="000.000.000-00"
                value={novoPaciente.cpf}
                onChange={(e) =>
                  setNovoPaciente({ ...novoPaciente, cpf: e.target.value })
                }
                mask="999.999.999-99"
                style={inputStyle}
              />
            </div>

            {/* Telefone */}
            <div style={{ display: "flex", flexDirection: "column" }}>
              <div style={labelStyle}>
                <FaPhone style={iconStyle} />
                <span>TELEFONE</span>
              </div>
              <InputMask
                placeholder="(00) 00000-0000"
                value={novoPaciente.telefone}
                mask="(99) 99999-9999"
                onChange={(e) =>
                  setNovoPaciente({
                    ...novoPaciente,
                    telefone: e.target.value,
                  })
                }
                style={inputStyle}
              />
            </div>

            {/* Email */}
            <div style={{ display: "flex", flexDirection: "column" }}>
              <div style={labelStyle}>
                <FaEnvelope style={iconStyle} />
                <span>E-MAIL</span>
              </div>
              <InputText
                value={novoPaciente.email}
                onChange={(e) =>
                  setNovoPaciente({
                    ...novoPaciente,
                    email: e.target.value,
                  })
                }
                placeholder="paciente@email.com"
                style={inputStyle}
              />
            </div>

            {/* ========== COLUNA 2 - DADOS DO AGENDAMENTO ========== */}

            {/* Empresa */}
            <div style={{ display: "flex", flexDirection: "column" }}>
              <div style={labelStyle}>
                <FaBuilding style={iconStyle} />
                <span>EMPRESA *</span>
              </div>
              <Dropdown
                value={empresa}
                onChange={(e) => {
                  setEmpresa(e.value);
                  setProfissional("");
                  setHora("");
                  setHorariosDisponiveis([]);
                }}
                options={
                  dadosCompletos.empresas?.map((emp) => ({
                    label: emp.nome,
                    value: emp.id,
                  })) || []
                }
                placeholder="Selecione a empresa"
                style={{
                  width: "100%",
                  height: "40px",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                }}
                itemTemplate={dropdownItemTemplate}
                valueTemplate={dropdownValueTemplate}
                appendTo={document.body} // Fix para mobile
              />
            </div>

            {/* Profissional */}
            <div style={{ display: "flex", flexDirection: "column" }}>
              <div style={labelStyle}>
                <FaUserMd style={iconStyle} />
                <span>PROFISSIONAL *</span>
              </div>
              <Dropdown
                value={profissional}
                onChange={handleProfissionalChange}
                options={profissionaisFiltrados}
                placeholder="Selecione o profissional"
                disabled={!empresa}
                style={{
                  width: "100%",
                  height: "40px",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  backgroundColor: !empresa ? "#f7fafc" : "#ffffff",
                }}
                itemTemplate={dropdownItemTemplate}
                valueTemplate={dropdownValueTemplate}
              />
            </div>

            {/* Data */}
            <div style={{ display: "flex", flexDirection: "column" }}>
              <div style={labelStyle}>
                <FaCalendarAlt style={iconStyle} />
                <span>DATA *</span>
              </div>
              <div style={{ position: "relative", width: "100%" }}>
                <Calendar
                  value={data ? new Date(data + "T00:00:00") : null}
                  onChange={handleDataChange}
                  dateFormat="dd/mm/yy"
                  placeholder="Selecione uma data"
                  showIcon
                  style={{ width: "100%" }}
                  inputStyle={{
                    width: "100%",
                    height: "40px",
                    fontFamily: "'Rajdhani', sans-serif",
                    fontSize: "0.9rem",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    padding: "0 12px",
                    borderTopRightRadius: "0",
                    borderBottomRightRadius: "0",
                  }}
                  panelStyle={{
                    fontFamily: "'Rajdhani', sans-serif",
                    fontSize: "0.9rem",
                  }}
                  numberOfMonths={1}
                  minDate={new Date()}
                />
              </div>
            </div>

            {/* Horário */}
            <div style={{ display: "flex", flexDirection: "column" }}>
              <div style={labelStyle}>
                <FaClock style={iconStyle} />
                <span>HORÁRIO *</span>
              </div>
              <Dropdown
                value={hora}
                onChange={(e) => setHora(e.value)}
                options={horariosDisponiveis}
                placeholder="Selecione o horário"
                disabled={!data || !profissional || horariosDisponiveis.length === 0}
                style={{
                  width: "100%",
                  height: "40px",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  backgroundColor: !data || !profissional ? "#f7fafc" : "#ffffff",
                }}
                itemTemplate={dropdownItemTemplate}
                valueTemplate={dropdownValueTemplate}
              />              
            </div>

            {/* Serviço - Ocupa 2 colunas no desktop, 1 no mobile */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gridColumn: isMobile ? "span 1" : "span 2",
              }}
            >
              <div style={labelStyle}>
                <FaList style={iconStyle} />
                <span>SERVIÇO *</span>
              </div>
              <Dropdown
                value={servico}
                onChange={(e) => setServico(e.value)}
                options={servicosFiltrados}
                placeholder="Selecione o serviço"
                disabled={!hora}
                style={{
                  width: "100%",
                  height: "40px",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  backgroundColor: !hora ? "#f7fafc" : "#ffffff",
                }}
                itemTemplate={dropdownItemTemplate}
                valueTemplate={dropdownValueTemplate}
              />
            </div>
          </div>
        </div>

        {/* ========== BOTÕES DE AÇÃO ========== */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "16px",
            flexWrap: "wrap",
            marginTop: "24px",
          }}
        >
          {/* Botão Cancelar */}
          <Button
            label="CANCELAR"
            icon="pi pi-times"
            onClick={handleCancelar}
            className="p-button-outlined"
            style={{
              padding: "10px 28px",
              minWidth: "120px",
              borderColor: "#e2e8f0",
              color: "#718096",
              fontWeight: "600",
              borderRadius: "8px",
            }}
            disabled={loading}
          />

          {/* Botão Criar Agendamento */}
          <Button
            label={loading ? "SALVANDO..." : "CRIAR AGENDAMENTO"}
            icon="pi pi-check"
            onClick={handleAdicionarAgenda}
            className="p-button-success p-button-sm"
            style={{
              padding: "10px 28px",
              minWidth: "180px",
              fontWeight: "600",
              borderRadius: "8px",
              color: "white",
            }}
            disabled={
              !novoPaciente.nome ||
              !novoPaciente.cpf ||
              !empresa ||
              !profissional ||
              !data ||
              !hora ||
              !servico ||
              loading
            }
            loading={loading}
          />
        </div>

        {/* ========== CSS PERSONALIZADO ========== */}
        <style>
          {`
            /* Ajuste do ícone do calendário para ficar do mesmo tamanho do input */
            .p-calendar .p-button.p-button-icon-only {
              width: 40px !important;
              height: 40px !important;
              background-color: #f7fafc !important;
              border: 1px solid #e2e8f0 !important;
              border-left: none !important;
              border-top-right-radius: 8px !important;
              border-bottom-right-radius: 8px !important;
              color: #B83732 !important;
            }
            .p-calendar .p-button.p-button-icon-only:hover {
              background-color: #edf2f7 !important;
            }
            .p-calendar .p-button-icon {
              font-size: 0.9rem !important;
            }
          `}
        </style>
      </>
    </Dialog>
  );
};

export default DialogNovaAgenda;
